import React, { createContext, useContext, useState, useCallback } from "react";

export interface AuthError {
  type: "tenant_access" | "user_not_found" | "session_expired" | "unauthorized";
  message: string;
  path?: string[];
  timestamp: number;
}

interface AuthErrorContextType {
  authError: AuthError | null;
  setAuthError: (error: AuthError | null) => void;
  clearAuthError: () => void;
  hasAuthError: boolean;
}

const AuthErrorContext = createContext<AuthErrorContextType | undefined>(
  undefined,
);

// Global event bus for auth errors (allows apollo-client to communicate with React)
// eslint-disable-next-line react-refresh/only-export-components
export const authErrorEmitter = {
  listeners: [] as ((error: AuthError) => void)[],
  emit(error: AuthError) {
    this.listeners.forEach((listener) => listener(error));
  },
  subscribe(listener: (error: AuthError) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  },
};

/**
 * Helper function to detect and categorize authentication/authorization errors
 */
// eslint-disable-next-line react-refresh/only-export-components
export function categorizeAuthError(
  message: string,
  path?: readonly string[],
): AuthError | null {
  const lowerMessage = message.toLowerCase();
  const timestamp = Date.now();
  const errorPath = path ? [...path] : undefined;

  if (
    lowerMessage.includes("user does not have access to this tenant") ||
    lowerMessage.includes("tenant access")
  ) {
    return {
      type: "tenant_access",
      message:
        "Your session has lost access to this organization. This may happen if your permissions were changed or your account was removed from the organization.",
      path: errorPath,
      timestamp,
    };
  }

  if (lowerMessage.includes("user not found")) {
    return {
      type: "user_not_found",
      message:
        "We couldn't verify your session. This usually happens when your login has expired or you're signed into a different account.",
      path: errorPath,
      timestamp,
    };
  }

  if (
    lowerMessage.includes("token expired") ||
    lowerMessage.includes("session expired") ||
    lowerMessage.includes("jwt expired")
  ) {
    return {
      type: "session_expired",
      message: "Your session has expired. Please log in again to continue.",
      path: errorPath,
      timestamp,
    };
  }

  if (
    lowerMessage.includes("unauthorized") ||
    lowerMessage.includes("authentication required") ||
    lowerMessage.includes("not authenticated")
  ) {
    return {
      type: "unauthorized",
      message:
        "You are not authorized to access this resource. Please log in again.",
      path: errorPath,
      timestamp,
    };
  }

  return null;
}

export const AuthErrorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authError, setAuthErrorState] = useState<AuthError | null>(null);

  // Subscribe to global auth error events from apollo-client
  React.useEffect(() => {
    const unsubscribe = authErrorEmitter.subscribe((error) => {
      setAuthErrorState(error);
    });
    return unsubscribe;
  }, []);

  const setAuthError = useCallback((error: AuthError | null) => {
    setAuthErrorState(error);
  }, []);

  const clearAuthError = useCallback(() => {
    setAuthErrorState(null);
  }, []);

  const value: AuthErrorContextType = {
    authError,
    setAuthError,
    clearAuthError,
    hasAuthError: authError !== null,
  };

  return (
    <AuthErrorContext.Provider value={value}>
      {children}
    </AuthErrorContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthError = () => {
  const context = useContext(AuthErrorContext);
  if (context === undefined) {
    throw new Error("useAuthError must be used within an AuthErrorProvider");
  }
  return context;
};
